import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HelpIcon from '@mui/icons-material/Help';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useDashboard, useSendInvites } from '../hooks/useDashboard.js';
import { useDeleteEvent } from '../hooks/useEvents.js';
import EmailActionsPanel from '../components/email/EmailActionsPanel.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const STATUS_COLORS: Record<string, 'default' | 'success' | 'info' | 'error'> = {
  DRAFT: 'default',
  ACTIVE: 'success',
  COMPLETED: 'info',
  CANCELLED: 'error',
};

const RSVP_CHIP = {
  YES: { color: 'success' as const, label: 'Yes' },
  NO: { color: 'error' as const, label: 'No' },
  MAYBE: { color: 'warning' as const, label: 'Maybe' },
  PENDING: { color: 'default' as const, label: 'Pending' },
};

function getRsvpChip(status: string) {
  if (status in RSVP_CHIP) return RSVP_CHIP[status as keyof typeof RSVP_CHIP];
  return RSVP_CHIP.PENDING;
}

function formatDateTime(dateStr: string, timezone?: string): string {
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: timezone,
  }).format(date);
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return '--';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
}

function pct(part: number, total: number): string {
  if (total === 0) return '0%';
  return `${Math.round((part / total) * 100)}%`;
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function DashboardSkeleton() {
  return (
    <Stack spacing={3}>
      <Skeleton variant="text" width="40%" height={48} />
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map((i) => (
          <Grid key={i} size={{ xs: 6, sm: 3 }}>
            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
          </Grid>
        ))}
      </Grid>
      <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 2 }} />
      <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// RSVP Summary Card
// ---------------------------------------------------------------------------

interface RsvpCardProps {
  count: number;
  label: string;
  color: string;
  icon: React.ReactNode;
}

function RsvpCard({ count, label, color, icon }: RsvpCardProps) {
  return (
    <Card
      sx={{
        textAlign: 'center',
        borderTop: 3,
        borderColor: color,
      }}
    >
      <CardContent sx={{ py: 2, px: 1, '&:last-child': { pb: 2 } }}>
        <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
        <Typography variant="h3" fontWeight={700} sx={{ color }}>
          {count}
        </Typography>
        <Typography variant="body2" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Sort helpers
// ---------------------------------------------------------------------------

type SortKey = 'name' | 'rsvpStatus';
type SortDir = 'asc' | 'desc';

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function EventDetailPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { data: dashboard, isLoading, isError, error } = useDashboard(eventId ?? '');
  const deleteEvent = useDeleteEvent();
  const sendInvites = useSendInvites();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendResult, setSendResult] = useState<{ sent: number; failed: number } | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Sort guests
  const sortedGuests = useMemo(() => {
    if (!dashboard) return [];
    const guests = [...dashboard.guests];
    const rsvpOrder: Record<string, number> = { YES: 0, MAYBE: 1, PENDING: 2, NO: 3 };
    guests.sort((a, b) => {
      let cmp: number;
      if (sortKey === 'rsvpStatus') {
        cmp = (rsvpOrder[a.rsvpStatus] ?? 4) - (rsvpOrder[b.rsvpStatus] ?? 4);
      } else {
        cmp = a.name.localeCompare(b.name);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return guests;
  }, [dashboard, sortKey, sortDir]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const handleDelete = async () => {
    if (!eventId) return;
    setDeleteError('');
    try {
      await deleteEvent.mutateAsync(eventId);
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setDeleteError((err as Error)?.message ?? 'Failed to delete event.');
    }
  };

  const handleSendInvites = async () => {
    if (!eventId) return;
    setSendResult(null);
    try {
      const result = await sendInvites.mutateAsync(eventId);
      setSendResult({ sent: result.sent, failed: result.failed });
    } catch {
      // Error is shown via sendInvites.error
    }
  };

  // Loading
  if (isLoading) {
    return <DashboardSkeleton />;
  }

  // Error
  if (isError) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        <Alert severity="error">
          {(error as Error)?.message ?? 'Failed to load event. It may not exist.'}
        </Alert>
      </Box>
    );
  }

  if (!dashboard) {
    return (
      <Box>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard')}
          sx={{ mb: 2 }}
        >
          Back to Dashboard
        </Button>
        <Alert severity="warning">Event not found.</Alert>
      </Box>
    );
  }

  const { event, rsvpSummary, guests, emailStats } = dashboard;
  const totalGuests = guests.length;
  const canSendInvites = (event.status === 'DRAFT' || event.status === 'ACTIVE') && totalGuests > 0;

  return (
    <Box>
      {/* ── Top section: title, status, date, location, actions ── */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/dashboard')}
            size="small"
            sx={{ mb: 1 }}
          >
            Dashboard
          </Button>
          <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
            <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight={700}>
              {event.title}
            </Typography>
            <Chip
              label={event.status}
              color={STATUS_COLORS[event.status] ?? 'default'}
              size="small"
            />
          </Stack>

          <Stack direction="row" alignItems="center" spacing={2} mt={1} flexWrap="wrap" useFlexGap>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <EventIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {formatDateTime(event.dateTime, event.timezone)}
              </Typography>
            </Stack>
            {event.location && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <LocationOnIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {event.location}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            disabled={!canSendInvites || sendInvites.isPending}
            onClick={() => setSendDialogOpen(true)}
          >
            Send Invites
          </Button>
          {/* Reminder scheduling is now in EmailActionsPanel below */}
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete
          </Button>
        </Stack>
      </Stack>

      {/* ── Empty state for drafts with no guests ── */}
      {event.status === 'DRAFT' && totalGuests === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Add guests and send invites to get started. Once you have guests, use the
          "Send Invites" button to notify them.
        </Alert>
      )}

      {/* ── RSVP Summary Cards ── */}
      <Grid container spacing={2} mb={3}>
        <Grid size={{ xs: 6, sm: 3 }}>
          <RsvpCard
            count={rsvpSummary.yes}
            label="Accepted"
            color={theme.palette.success.main}
            icon={<CheckCircleIcon />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <RsvpCard
            count={rsvpSummary.no}
            label="Declined"
            color={theme.palette.error.main}
            icon={<CancelIcon />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <RsvpCard
            count={rsvpSummary.maybe}
            label="Maybe"
            color={theme.palette.warning.main}
            icon={<HelpIcon />}
          />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <RsvpCard
            count={rsvpSummary.pending}
            label="Pending"
            color={theme.palette.grey[500]}
            icon={<HourglassEmptyIcon />}
          />
        </Grid>
      </Grid>

      {/* ── Email Stats ── */}
      {emailStats.sent > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Email Performance
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                icon={<MailOutlineIcon />}
                label={`Sent: ${emailStats.sent}`}
                variant="outlined"
                size="small"
              />
              <Chip
                label={`Delivered: ${emailStats.delivered}`}
                variant="outlined"
                size="small"
                color="success"
              />
              <Chip
                icon={<VisibilityIcon />}
                label={`Opened: ${emailStats.opened} (${pct(emailStats.opened, emailStats.sent)})`}
                variant="outlined"
                size="small"
                color="info"
              />
              <Chip
                icon={<TouchAppIcon />}
                label={`Clicked: ${emailStats.clicked}`}
                variant="outlined"
                size="small"
                color="primary"
              />
              {emailStats.bounced > 0 && (
                <Chip
                  icon={<WarningAmberIcon />}
                  label={`Bounced: ${emailStats.bounced}`}
                  variant="outlined"
                  size="small"
                  color="error"
                />
              )}
              {emailStats.failed > 0 && (
                <Chip
                  label={`Failed: ${emailStats.failed}`}
                  variant="outlined"
                  size="small"
                  color="error"
                />
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* ── Email Actions Panel ── */}
      <EmailActionsPanel eventId={eventId!} eventStatus={event.status} />

      {/* ── Guest Table ── */}
      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              Guest List
            </Typography>
            <Chip
              label={`${totalGuests} guest${totalGuests !== 1 ? 's' : ''}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Stack>

          {totalGuests === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              No guests added yet.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortKey === 'name'}
                        direction={sortKey === 'name' ? sortDir : 'asc'}
                        onClick={() => handleSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>}
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortKey === 'rsvpStatus'}
                        direction={sortKey === 'rsvpStatus' ? sortDir : 'asc'}
                        onClick={() => handleSort('rsvpStatus')}
                      >
                        RSVP
                      </TableSortLabel>
                    </TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 600 }}>RSVP Date</TableCell>}
                    <TableCell sx={{ fontWeight: 600 }}>Email Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedGuests.map((guest) => {
                    const chip = getRsvpChip(guest.rsvpStatus);
                    return (
                      <TableRow key={guest.id} hover>
                        <TableCell>
                          {guest.name}
                          {isMobile && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {guest.email}
                            </Typography>
                          )}
                        </TableCell>
                        {!isMobile && <TableCell>{guest.email}</TableCell>}
                        <TableCell>
                          <Chip
                            label={chip.label}
                            color={chip.color}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        {!isMobile && (
                          <TableCell>
                            {formatShortDate(guest.rsvpDate)}
                          </TableCell>
                        )}
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            {guest.emailSentAt && (
                              <Tooltip title={`Sent ${formatShortDate(guest.emailSentAt)}`}>
                                <MailOutlineIcon
                                  fontSize="small"
                                  color="action"
                                />
                              </Tooltip>
                            )}
                            {guest.emailOpened && (
                              <Tooltip title="Opened">
                                <VisibilityIcon
                                  fontSize="small"
                                  color="info"
                                />
                              </Tooltip>
                            )}
                            {guest.emailClicked && (
                              <Tooltip title="Clicked">
                                <TouchAppIcon
                                  fontSize="small"
                                  color="primary"
                                />
                              </Tooltip>
                            )}
                            {guest.emailBounced && (
                              <Tooltip title="Bounced">
                                <WarningAmberIcon
                                  fontSize="small"
                                  color="error"
                                />
                              </Tooltip>
                            )}
                            {!guest.emailSentAt && (
                              <Typography variant="caption" color="text.secondary">
                                --
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Send Invites Dialog ── */}
      <Dialog
        open={sendDialogOpen}
        onClose={() => {
          if (!sendInvites.isPending) {
            setSendDialogOpen(false);
            setSendResult(null);
          }
        }}
      >
        <DialogTitle>Send Invites</DialogTitle>
        <DialogContent>
          {!sendResult && !sendInvites.isPending && (
            <DialogContentText>
              Send invites to <strong>{totalGuests}</strong> guest
              {totalGuests !== 1 ? 's' : ''}?
            </DialogContentText>
          )}
          {sendInvites.isPending && (
            <Stack alignItems="center" spacing={2} py={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Sending invites...
              </Typography>
            </Stack>
          )}
          {sendResult && (
            <Alert severity={sendResult.failed > 0 ? 'warning' : 'success'} sx={{ mt: 1 }}>
              {sendResult.sent} sent{sendResult.failed > 0 ? `, ${sendResult.failed} failed` : ''}
            </Alert>
          )}
          {sendInvites.isError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {(sendInvites.error as Error)?.message ?? 'Failed to send invites'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {!sendResult ? (
            <>
              <Button
                onClick={() => setSendDialogOpen(false)}
                disabled={sendInvites.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendInvites}
                variant="contained"
                disabled={sendInvites.isPending}
              >
                Confirm
              </Button>
            </>
          ) : (
            <Button
              onClick={() => {
                setSendDialogOpen(false);
                setSendResult(null);
              }}
              variant="contained"
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{event.title}&quot;? This action cannot be undone.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteEvent.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={deleteEvent.isPending}
            startIcon={deleteEvent.isPending ? <CircularProgress size={18} color="inherit" /> : undefined}
          >
            {deleteEvent.isPending ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
